import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  TableLoader,
  TablePagination,
} from '../../../components/ui';
import { findBuById } from '../../../data/master/businessUnits';
import { STAFF_SCOPE_TYPES } from '../../../data/staff/permissions';
import { setRolesCache } from '../../../data/staff/roles';
import { usePagination } from '../../../hooks/usePagination';
import { useTableSort } from '../../../hooks/useTableSort';
import { lookupBusinessUnits, lookupSites } from '../../../repositories/lookup.repository';
import { listRoles } from '../../../repositories/role.repository';
import { listStaff } from '../../../repositories/staff.repository';
import { formatStaffScope } from '../../../utils/staffScope';
import { StaffFormModal } from './StaffFormModal';
import { StaffPasswordModal } from './StaffPasswordModal';

export default function StaffManagementPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const passwordModal = useDisclosure();
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStaff, setEditingStaff] = useState(null);
  const [passwordStaff, setPasswordStaff] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffResult, rolesResult, buResult, sitesResult] = await Promise.all([
        listStaff({ page: 1, pageSize: 200 }),
        listRoles({ page: 1, pageSize: 200 }),
        lookupBusinessUnits(),
        lookupSites(),
      ]);
      const roleItems = rolesResult.items || [];
      setStaffList(staffResult.items || []);
      setRoles(roleItems);
      setRolesCache(roleItems);
      setBusinessUnits(buResult || []);
      setSites(sitesResult || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load staff',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: r.roleId,
        label: r.name,
        description: r.roleId,
      })),
    [roles],
  );

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst,
        })),
    [businessUnits],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staffList.filter((s) => {
      const matchQ =
        !q ||
        (s.fullName || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.staffId || '').toLowerCase().includes(q) ||
        (s.mobile || '').includes(q);
      const matchRole = !roleFilter || (s.roleIds || []).includes(roleFilter);
      const matchScope = !scopeFilter || s.scopeType === scopeFilter;
      const matchBu = !buFilter || s.buId === buFilter;
      const matchActive =
        !activeFilter ||
        (activeFilter === 'Active' && s.active !== false) ||
        (activeFilter === 'Inactive' && s.active === false);
      return matchQ && matchRole && matchScope && matchBu && matchActive;
    });
  }, [staffList, search, roleFilter, scopeFilter, buFilter, activeFilter]);

  const { sorted, sort, toggleSort } = useTableSort(filtered, {
    staffId: (s) => s.staffId,
    fullName: (s) => s.fullName,
    roles: (s) =>
      (s.roleIds || [])
        .map((id) => roles.find((r) => r.roleId === id)?.name || id)
        .join(', '),
    scope: (s) => formatStaffScope(s, businessUnits, sites),
    bu: (s) => findBuById(businessUnits, s.buId)?.shortName,
    status: (s) => (s.active !== false ? 'Active' : 'Inactive'),
    lastLogin: (s) => s.lastLoginAt,
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingStaff(null);
    formModal.onOpen();
  };

  const openEdit = (row) => {
    setEditingStaff(row);
    formModal.onOpen();
  };

  const openPassword = (row) => {
    setPasswordStaff(row);
    passwordModal.onOpen();
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setScopeFilter('');
    setBuFilter('');
    setActiveFilter('');
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size={{ base: 'lg', xl: 'xl' }}>Staff management</Heading>
          <Text color="text.secondary" mt={2} maxW="760px" fontSize={{ base: '14px', xl: '15px' }}>
            Internal admin users — roles (what they can do) and scope (where: All, BU, or BU +
            Site).
          </Text>
        </Box>
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
          Add staff user
        </PrimaryButton>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Name / email / ID / mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Role"
            labelFontSize="13px"
            h="40px"
            placeholder="All roles"
            value={roleFilter}
            onChange={(v) => setRoleFilter(v ?? '')}
            options={[{ value: '', label: 'All roles' }, ...roleOptions]}
            isSearchable
          />
          <AppSelect
            label="Scope type"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={scopeFilter}
            onChange={(v) => setScopeFilter(v ?? '')}
            options={[{ value: '', label: 'All scopes' }, ...STAFF_SCOPE_TYPES]}
          />
          <AppSelect
            label="Business Unit"
            labelFontSize="13px"
            h="40px"
            placeholder="All BUs"
            value={buFilter}
            onChange={(v) => setBuFilter(v ?? '')}
            options={[{ value: '', label: 'All BUs' }, ...buOptions]}
            isSearchable
          />
          <AppSelect
            label="Status"
            labelFontSize="13px"
            h="40px"
            value={activeFilter}
            onChange={(v) => setActiveFilter(v ?? '')}
            options={[
              { value: '', label: 'All' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={6} />
        ) : (
          <>
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
            {`${totalCount} staff user${totalCount === 1 ? '' : 's'}`}
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="staffId" sort={sort} onSort={toggleSort}>
                  Staff ID
                </SortableTh>
                <SortableTh columnKey="fullName" sort={sort} onSort={toggleSort}>
                  Name
                </SortableTh>
                <SortableTh columnKey="roles" sort={sort} onSort={toggleSort}>
                  Role(s)
                </SortableTh>
                <SortableTh columnKey="scope" sort={sort} onSort={toggleSort}>
                  Data scope
                </SortableTh>
                <SortableTh columnKey="bu" sort={sort} onSort={toggleSort}>
                  BU
                </SortableTh>
                <SortableTh columnKey="status" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <SortableTh columnKey="lastLogin" sort={sort} onSort={toggleSort}>
                  Last login
                </SortableTh>
                <Th w="96px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map((row) => {
                const bu = findBuById(businessUnits, row.buId);
                const scopeLabel = formatStaffScope(row, businessUnits, sites);
                return (
                  <Tr
                    key={row.id ?? row.staffId}
                    _hover={{ bg: 'bg.input' }}
                    cursor="pointer"
                    onClick={() => navigate(`/admin/administration/staff/${row.id}`)}
                  >
                    <Td fontFamily="mono" fontSize="13px">
                      {row.staffId}
                    </Td>
                    <Td>
                      <Text fontWeight="600" fontSize="14px">
                        {row.fullName}
                      </Text>
                      <Text fontSize="12px" color="text.secondary">
                        {row.email}
                      </Text>
                      <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                        {row.mobile}
                      </Text>
                    </Td>
                    <Td fontSize="13px" maxW="200px">
                      {(row.roleIds || []).map((id) => {
                        const role = roles.find((r) => r.roleId === id);
                        return (
                          <StatusBadge key={id} status="info" mr={1} mb={1}>
                            {role?.name || id}
                          </StatusBadge>
                        );
                      })}
                    </Td>
                    <Td>
                      <Text fontWeight="600" fontSize="13px">
                        {scopeLabel}
                      </Text>
                      <Text fontSize="12px" color="text.secondary" mt={0.5}>
                        {STAFF_SCOPE_TYPES.find((t) => t.value === row.scopeType)?.label?.split(
                          ' —',
                        )[0] || row.scopeType}
                      </Text>
                    </Td>
                    <Td fontSize="13px">{bu?.shortName || (row.scopeType === 'all' ? '—' : '—')}</Td>
                    <Td>
                      <StatusBadge status={row.active !== false ? 'verified' : 'neutral'}>
                        {row.active !== false ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </Td>
                    <Td fontSize="12px" color="text.secondary" whiteSpace="nowrap">
                      {row.lastLoginAt
                        ? String(row.lastLoginAt).slice(0, 16).replace('T', ' ')
                        : 'Never'}
                    </Td>
                    <Td>
                      <Flex gap={0}>
                        <IconButton
                          aria-label="Edit staff"
                          icon={<MdEdit />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(row);
                          }}
                        />
                        <IconButton
                          aria-label="Update password"
                          icon={<MdLock />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPassword(row);
                          }}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
              {!loading && pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="text.secondary" py={10} textAlign="center">
                      No staff users match your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
          </>
        )}
      </AppCard>

      <StaffFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        staff={editingStaff}
        roles={roles}
        businessUnits={businessUnits}
        sites={sites}
        onSaved={loadData}
      />
      <StaffPasswordModal
        isOpen={passwordModal.isOpen}
        onClose={passwordModal.onClose}
        staff={passwordStaff}
        onSaved={loadData}
      />
    </Box>
  );
}
